import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { Modal, Select } from '../ui';
import Button from '../Button';
import type { Employee, EmployeePayload, EmployeeStatus } from '../../hooks/useEmployees';

interface Dept { id: string; name: string; code: string }
interface UserLite { id: string; name: string; email: string }

const STATUS_OPTS: { value: EmployeeStatus; label: string }[] = [
  { value: 'active',     label: 'Active' },
  { value: 'inactive',   label: 'Inactive' },
  { value: 'on_leave',   label: 'On Leave' },
  { value: 'terminated', label: 'Terminated' },
];

const EMP_TYPE_OPTS = [
  { value: 'full_time',  label: 'Full-time' },
  { value: 'part_time',  label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern',     label: 'Intern' },
];

const inputCls =
  'w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Existing employee to edit, or null to create. */
  employee: Employee | null;
  /** All employees, for the manager picker. */
  employees: Employee[];
  onSave: (payload: EmployeePayload, id?: string) => Promise<boolean>;
}

type FormState = {
  name: string; email: string; employeeId: string; designation: string;
  employmentType: string; location: string; costCenter: string;
  departmentId: string; managerEmployeeId: string; hireDate: string;
  status: EmployeeStatus; userId: string;
};

const empty: FormState = {
  name: '', email: '', employeeId: '', designation: '', employmentType: '',
  location: '', costCenter: '', departmentId: '', managerEmployeeId: '',
  hireDate: '', status: 'active', userId: '',
};

export default function EmployeeFormModal({ open, onClose, employee, employees, onSave }: Props) {
  const [form, setForm] = useState<FormState>(empty);
  const [depts, setDepts] = useState<Dept[]>([]);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [saving, setSaving] = useState(false);

  const isEdit = !!employee;

  // Load reference data (departments + linkable users) once when opened.
  useEffect(() => {
    if (!open) return;
    api.get<Dept[]>('/admin/departments').then(r => setDepts(r.data ?? [])).catch(() => setDepts([]));
    api.get('/admin/users', { params: { limit: 200, page: 1 } })
      .then(r => setUsers((r.data?.users ?? r.data ?? []) as UserLite[]))
      .catch(() => setUsers([]));
  }, [open]);

  // Seed the form from the employee being edited (or reset for create).
  useEffect(() => {
    if (!open) return;
    if (employee) {
      setForm({
        name:           employee.name ?? employee.user?.name ?? '',
        email:          employee.email ?? employee.user?.email ?? '',
        employeeId:     employee.employeeId ?? '',
        designation:    employee.designation ?? '',
        employmentType: employee.employmentType ?? '',
        location:       employee.location ?? '',
        costCenter:     employee.costCenter ?? '',
        departmentId:   employee.departmentId ?? employee.department?.id ?? '',
        managerEmployeeId: employee.managerEmployeeId ?? employee.manager?.id ?? '',
        hireDate:       employee.hireDate ? employee.hireDate.slice(0, 10) : '',
        status:         employee.status ?? 'active',
        userId:         employee.userId ?? employee.user?.id ?? '',
      });
    } else {
      setForm(empty);
    }
  }, [open, employee]);

  const deptOpts = useMemo(() => depts.map(d => ({ value: d.id, label: d.name })), [depts]);
  const userOpts = useMemo(
    () => users.map(u => ({ value: u.id, label: `${u.name} (${u.email})` })),
    [users],
  );
  const managerOpts = useMemo(
    () => employees
      .filter(e => e.id !== employee?.id)
      .map(e => ({ value: e.id, label: `${e.name ?? e.user?.name ?? e.employeeId} · ${e.employeeId}` })),
    [employees, employee?.id],
  );

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload: EmployeePayload = {
      name:           form.name.trim(),
      email:          form.email.trim() || null,
      userId:         form.userId || null,
      employeeId:     form.employeeId.trim() || null,
      designation:    form.designation.trim() || null,
      employmentType: form.employmentType || null,
      location:       form.location.trim() || null,
      costCenter:     form.costCenter.trim() || null,
      departmentId:   form.departmentId || null,
      managerEmployeeId: form.managerEmployeeId || null,
      hireDate:       form.hireDate || null,
      status:         form.status,
    };
    const ok = await onSave(payload, employee?.id);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Edit Employee' : 'New Employee'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create employee'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Full Name <span className="text-red-500">*</span></label>
          <input className={inputCls} value={form.name} onChange={set('name')} autoFocus />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input className={inputCls} type="email" value={form.email} onChange={set('email')} />
        </div>
        <div>
          <label className={labelCls}>Employee ID</label>
          <input className={inputCls} value={form.employeeId} onChange={set('employeeId')} placeholder="Auto-generated if blank" />
        </div>
        <div>
          <label className={labelCls}>Designation</label>
          <input className={inputCls} value={form.designation} onChange={set('designation')} />
        </div>
        <Select label="Department" placeholder="Select…" options={deptOpts} value={form.departmentId} onChange={set('departmentId')} />
        <Select label="Employment Type" placeholder="Select…" options={EMP_TYPE_OPTS} value={form.employmentType} onChange={set('employmentType')} />
        <Select label="Status" options={STATUS_OPTS} value={form.status} onChange={set('status')} />
        <div>
          <label className={labelCls}>Location</label>
          <input className={inputCls} value={form.location} onChange={set('location')} />
        </div>
        <div>
          <label className={labelCls}>Cost Center</label>
          <input className={inputCls} value={form.costCenter} onChange={set('costCenter')} />
        </div>
        <div>
          <label className={labelCls}>Hire Date</label>
          <input className={inputCls} type="date" value={form.hireDate} onChange={set('hireDate')} />
        </div>
        <Select label="Manager" placeholder="None" options={managerOpts} value={form.managerEmployeeId} onChange={set('managerEmployeeId')} />
        <div className="sm:col-span-2 border-t border-gray-100 pt-3">
          <Select
            label="Link to portal user (optional)"
            placeholder="Not linked"
            options={userOpts}
            value={form.userId}
            onChange={set('userId')}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Link this record to a portal account for login. Leave blank for staff without portal access.
          </p>
        </div>
      </div>
    </Modal>
  );
}
